import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { formatPhoneNumber } from '../../../modules/helpers';
import Accordion from '../../shared/Accordion';
import AccordionItem from '../../shared/AccordionItem';
import accordionFaqTheme from '../../shared/Accordion-truckFaq.module.postcss';

import styles from './Truck.module.postcss';

function TruckFaq({ truck }) {

  const getFoodOptionsText = () => {
    const optionsOffered = [];
    if (truck.vegan) optionsOffered.push('vegan');
    if (truck.vegetarian) optionsOffered.push('vegetarian');
    if (truck.gluten_free) optionsOffered.push('gluten-free');

    let optionsText = '';
    switch (optionsOffered.length) {
      case 1:
        optionsText = `Yes, ${truck.name} offers ${optionsOffered[0]} options.`;
        break;
      case 2:
        optionsText = `Yes, ${truck.name} offers ${optionsOffered[0]} and ${optionsOffered[1]} options.`;
        break;
      case 3:
        optionsText = `Yes, ${truck.name} offers vegan, vegetarian, and gluten-free options.`;
        break;
      default:
        optionsText = `No, ${truck.name} does not offer vegan, vegetarian, or gluten-free options.`;
        break;
    }

    return optionsText;
  };

  const q1 = {
    question: `Does ${truck.name} offer vegetarian, vegan, or gluten-free options?`,
    answer: getFoodOptionsText()
  };
  const q2 = {
    question: `Does ${truck.name} cater?`,
    answer: `Send ${truck.name} a catering request with your date, location, and guest count. If they’re taking catering bookings, they can reply with a quote, and you’ll see the full price, including fees, before you book.`
  };
  const q3 = {
    question: 'What forms of payment are accepted?',
    answer: `${truck.name} ${truck.accepts_credit_cards ? 'accepts' : 'does not accept'} credit cards.`
  };
  const formattedPhone = formatPhoneNumber(truck.phone);
  const q4 = {
    question: `What is ${truck.name}’s phone number?`,
    answer: truck.phone ? <a href={`tel:${truck.phone}`}>{formattedPhone}</a> : formattedPhone
  };

  const metaJSON = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: q1.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `<p>${q1.answer}</p>`
      }
    },
    {
      '@type': 'Question',
      name: q2.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `<p>${q2.answer}</p>`
      }
    }, {
      '@type': 'Question',
      name: q3.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `<p>${q3.answer}</p>`
      }
    }]
  };

  if (truck.phone) {
    metaJSON.mainEntity.push({
      '@type': 'Question',
      name: q4.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `<p>${q4.answer}</p>`
      }
    });
  }

  const meta = (
    <script type="application/ld+json">
      {JSON.stringify(metaJSON)}
    </script>
  );

  return (
    <div className={styles.TruckFaq + ' u-mt2'}>
      <div className={styles.TruckFaq_header}>
        <h2>Frequently asked questions</h2>
      </div>

      <div className={styles.TruckFaq_content}>
        <Accordion theme={accordionFaqTheme}>
          <AccordionItem title={q1.question}>{ q1.answer }</AccordionItem>
          <AccordionItem title={q2.question}>{ q2.answer }</AccordionItem>
          <AccordionItem title={q3.question}>{ q3.answer }</AccordionItem>
          { truck.phone && <AccordionItem title={q4.question}>{ q4.answer }</AccordionItem> }
        </Accordion>
      </div>

      <Helmet>
        {meta}
      </Helmet>
    </div>
  );
}

TruckFaq.propTypes = {
  truck: PropTypes.object.isRequired,
};

export default TruckFaq;
